/**
 * ConferenciaDetailPage — Detail view for a single conferência.
 * Scanner, lacres, items, admin fields, finalization.
 * Two-read barcode support, LastReadCard, ScanDebugPanel.
 */
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { useConferenciaDetail } from '../hooks/useConferenciaDetail';
import { ConferenciaVerifHeader as ConferenciaHeader } from '../components/conferencia/ConferenciaVerifHeader';
import { ConferenciaAdminFields } from '../components/conferencia/ConferenciaAdminFields';
import { LacreSelector } from '../components/conferencia/LacreSelector';
import { ConferenciaItemsTable } from '../components/conferencia/ConferenciaItemsTable';
import { BarcodeScannerV2 as BarcodeScanner } from '../components/conferencia/BarcodeScannerV2';
import { LastReadCard } from '../components/conferencia/LastReadCard';
import { ScanDebugPanel } from '../components/conferencia/ScanDebugPanel';
import { ArrowLeft, Lock, LockOpen, CheckCircle, Edit3, RotateCcw, Volume2, Volume1, VolumeX } from 'lucide-react';
import { FloatingActionButtons } from '../components/contagem/FloatingActionButtons';
import { Popover, Slider } from '@mantine/core';
import { getVolume, setVolume as setSoundVolume, playTestBeep } from '../utils/soundFeedback';
import useAuthStore from '../stores/authStore';
import PermissionGate from '../components/PermissionGate';

export const ConferenciaDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const conferenciaId = id ? parseInt(id, 10) : null;

  const { user } = useAuthStore();
  const isMaster = user?.role === 'MASTER';
  const canEditItems = isMaster || user?.role === 'CONFERENTE';

  const {
    conferencia,
    itens,
    lacresDisponiveis,
    loading,
    loadingItens,
    saving,
    lastScannedItemId,
    scanInputRef,
    lastReadInfo,
    setLastReadInfo,
    scanLogs,
    clearScanLogs,
    handleSelectLacres,
    handleUpdateFields,
    handleScan,
    handleFinalize,
    handleToggleConferido,
    handleInitSound,
    // Edit mode
    isEditMode,
    modifiedItems,
    handleEnterEditMode,
    handleUpdateItemField,
    handleSaveAllItems,
    handleDiscardChanges,
    handleReiniciar,
  } = useConferenciaDetail(conferenciaId);

  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [volume, setVolumeState] = useState(() => Math.round(getVolume() * 100));

  const handleVolumeChange = useCallback((val: number) => {
    setVolumeState(val);
    setSoundVolume(val / 100);
  }, []);

  const VolumeIcon = volume <= 0 ? VolumeX : volume < 50 ? Volume1 : Volume2;

  const onFinalize = async () => {
    const ok = await handleFinalize();
    if (ok) setShowFinalizeDialog(false);
  };

  const onReset = async () => {
    await handleReiniciar();
    setShowResetDialog(false);
  };

  if (loading) {
    return (
      <MainLayout className="dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando conferência...</p>
        </div>
      </MainLayout>
    );
  }

  if (!conferencia) {
    return (
      <MainLayout>
        <div className="p-4 text-center">
          <p className="text-gray-600 dark:text-gray-400">Conferência não encontrada.</p>
          <button
            onClick={() => navigate('/cloud/conferencia')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Voltar para lista
          </button>
        </div>
      </MainLayout>
    );
  }

  const isReadOnly = conferencia.finalizada && !isMaster;
  const canScan = !conferencia.finalizada || isMaster;

  return (
    <MainLayout>
      <div className="p-2 space-y-4">
        {/* Back + Title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/cloud/conferencia')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Conferência #{conferencia.numero_conferencia || '?'}
          </h2>
          {conferencia.finalizada && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-sm font-medium">
              <Lock className="w-3.5 h-3.5" />
              Finalizada
            </span>
          )}
          {canEditItems && !conferencia.finalizada && !isEditMode && (
            <PermissionGate tela="CONFERENCIA_VIEW" acao="editar">
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={handleEnterEditMode}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Editar Contados
                </button>
                <button
                  onClick={() => setShowResetDialog(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reiniciar
                </button>
              </div>
            </PermissionGate>
          )}
        </div>

        {/* Header Card */}
        <ConferenciaHeader conferencia={conferencia} />

        {/* Admin Fields */}
        <ConferenciaAdminFields
          conferencia={conferencia}
          onSave={handleUpdateFields}
          readOnly={isReadOnly}
          saving={saving}
        />

        {/* Lacre Selector (only for LACRE type) */}
        {conferencia.tipo === 'LACRE' && lacresDisponiveis.length > 0 && (
          <LacreSelector
            lacres={lacresDisponiveis}
            onSelect={handleSelectLacres}
            readOnly={isReadOnly}
          />
        )}

        {/* Scanner + Volume Control */}
        {canScan && (
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <BarcodeScanner
                ref={scanInputRef}
                onScan={handleScan}
                onInitSound={handleInitSound}
                disabled={isReadOnly}
              />
            </div>
            <Popover opened={volumeOpen} onChange={setVolumeOpen} position="bottom-end" withArrow shadow="md" width={220}>
              <Popover.Target>
                <button
                  onClick={() => setVolumeOpen((o) => !o)}
                  className="mt-1 p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  title="Volume dos sons"
                >
                  <VolumeIcon size={18} className="text-gray-500 dark:text-gray-400" />
                </button>
              </Popover.Target>
              <Popover.Dropdown>
                <div className="py-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Volume</span>
                    <span className="text-xs text-gray-500">{volume}%</span>
                  </div>
                  <Slider
                    value={volume}
                    onChange={handleVolumeChange}
                    min={0}
                    max={100}
                    step={5}
                    label={(v) => `${v}%`}
                    color="blue"
                  />
                  <button
                    onClick={playTestBeep}
                    className="mt-3 w-full text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Testar som
                  </button>
                </div>
              </Popover.Dropdown>
            </Popover>
          </div>
        )}

        {/* Last Read Feedback Card */}
        {canScan && (
          <LastReadCard
            info={lastReadInfo}
            onDismiss={() => setLastReadInfo(null)}
          />
        )}

        {/* Debug Scanner Panel */}
        {canScan && (
          <ScanDebugPanel
            logs={scanLogs}
            onClear={clearScanLogs}
          />
        )}

        {/* Items Table */}
        <ConferenciaItemsTable
          itens={itens}
          loading={loadingItens}
          lastScannedItemId={lastScannedItemId}
          isEditMode={isEditMode}
          modifiedItems={modifiedItems}
          onUpdateItemField={handleUpdateItemField}
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 pb-6">
          {isMaster && conferencia.finalizada && (
            <PermissionGate tela="CONFERENCIA_VIEW" acao="editar">
              <button
                onClick={handleToggleConferido}
                className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
              >
                <LockOpen className="w-4 h-4" />
                Reabrir
              </button>
            </PermissionGate>
          )}
          {!conferencia.finalizada && (
            <PermissionGate tela="CONFERENCIA_VIEW" acao="editar">
              <button
                onClick={() => setShowFinalizeDialog(true)}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Finalizar Conferência
              </button>
            </PermissionGate>
          )}
        </div>

        {/* Edit Mode FABs */}
        {isEditMode && (
          <FloatingActionButtons
            onSaveAll={handleSaveAllItems}
            onDiscardChanges={handleDiscardChanges}
          />
        )}

        {/* Reset Confirmation Dialog */}
        {showResetDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Reiniciar Conferência?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Conferência #{conferencia.numero_conferencia}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                Todos os {itens.length} itens terão quantidade conferida zerada. Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowResetDialog(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={onReset}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  Confirmar Reinício
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Finalize Confirmation Dialog */}
        {showFinalizeDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                Finalizar Conferência?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Conferência #{conferencia.numero_conferencia}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Itens conferidos: {itens.filter(i => i.quantidade_conferida > 0).length} de {itens.length}
              </p>
              {itens.some(i => i.quantidade_conferida > 0 && i.diferenca !== 0) && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                  Existem itens com diferença. Deseja finalizar mesmo assim?
                </p>
              )}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowFinalizeDialog(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={onFinalize}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                >
                  Confirmar Finalização
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
